import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, Label, Message, Divider, Modal } from 'semantic-ui-react';

class PasswordRecoveryMethods extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    profileBusy: PropTypes.bool,
    recoveryStatus: PropTypes.string.isRequired,
    refreshStatus: PropTypes.func,
    setupEmailDestination: PropTypes.string,
    requestSetupEmail: PropTypes.func,
    hasPendingChanges: PropTypes.bool,
    temporaryUnavailable: PropTypes.bool,
    outcomeUnknown: PropTypes.bool,
    emailBlocked: PropTypes.bool,
    onEditTemporary: PropTypes.func.isRequired,
    onClearTemporary: PropTypes.func.isRequired,
    onBusyChange: PropTypes.func,
    saveTemporary: PropTypes.func,
    children: PropTypes.node,
    passwordUpdateStatus: PropTypes.node,
  };
  static defaultProps = { onBusyChange: () => {} };
  state = {
    method: null, pending: false, slow: false, result: null, cooldown: false, blocked: false,
  };
  componentDidMount() {
    this.mounted = true;
    if (this.props.refreshStatus) this.props.refreshStatus();
  }
  componentDidUpdate(prevProps) {
    const eligible = ['ready', 'setup_required'].includes(this.props.recoveryStatus) &&
      !this.props.temporaryUnavailable;
    if (!eligible && (prevProps.recoveryStatus !== this.props.recoveryStatus ||
      prevProps.temporaryUnavailable !== this.props.temporaryUnavailable)) {
      this.props.onClearTemporary();
      // Eligibility changes invalidate the open editor, including its secret draft.
      // eslint-disable-next-line react/no-did-update-set-state
      if (!this.state.pending) this.setState({ method: null, result: null });
    }
  }
  componentWillUnmount() {
    this.mounted = false;
    clearTimeout(this.slowTimer); clearTimeout(this.cooldownTimer);
  }
  choose = (method, trigger) => {
    const p = this.props;
    if (this.state.pending || p.profileBusy || p.outcomeUnknown || p.temporaryUnavailable || p.hasPendingChanges || !['ready', 'setup_required'].includes(p.recoveryStatus)) return;
    if (method === 'email' && (!p.setupEmailDestination || p.emailBlocked || this.state.cooldown || this.state.blocked)) return;
    this.reviewTrigger = trigger;
    if (method === 'temporary') this.props.onEditTemporary(); else p.onClearTemporary();
    this.setState({ method, result: null, slow: false });
  };
  close = () => {
    if (this.state.pending || this.props.profileBusy) return;
    if (this.state.method === 'temporary') this.props.onClearTemporary();
    this.setState({ method: null });
    if (this.props.refreshStatus) this.props.refreshStatus();
  };
  send = async () => {
    const p = this.props;
    if (this.sending || this.state.result || this.state.pending || this.state.cooldown || this.state.blocked || p.emailBlocked || p.profileBusy || p.hasPendingChanges || p.outcomeUnknown || p.temporaryUnavailable || !['ready', 'setup_required'].includes(p.recoveryStatus) || !p.setupEmailDestination || !this.props.requestSetupEmail) return;
    if (!window.crypto || !window.crypto.getRandomValues) { this.setState({ result: { state: 'unavailable' } }); return; }
    const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
    const requestID = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    this.sending = true;
    const onBusyChange = this.props.onBusyChange;
    onBusyChange(true, requestID);
    this.setState({ pending: true, slow: false }, () => {
      if (this.resultPanel) this.resultPanel.focus();
    });
    this.slowTimer = setTimeout(() => { if (this.mounted) this.setState({ slow: true }); }, 15000);
    let result;
    try {
      const response = await this.props.requestSetupEmail(requestID);
      const state = response && ['accepted', 'cooldown', 'daily_limit', 'not_eligible', 'unavailable'].includes(response.state) ? response.state : 'uncertain';
      result = { state, destination: response && response.destination };
    } catch (e) {
      result = { state: ['not_eligible', 'unavailable'].includes(e.setupState) ? e.setupState : 'uncertain' };
    } finally {
      clearTimeout(this.slowTimer); this.sending = false; onBusyChange(false, requestID);
      if (this.mounted) {
        const cooldown = ['accepted', 'cooldown', 'uncertain'].includes(result.state);
        this.setState({ pending: false, slow: false, result, cooldown, blocked: ['uncertain', 'daily_limit'].includes(result.state) });
        if (cooldown) {
          this.cooldownTimer = setTimeout(() => {
            if (this.mounted) this.setState({ cooldown: false });
          }, 60000);
        }
      }
    }
  };
  saveManual = async () => {
    if (this.sending || this.state.pending || this.props.profileBusy ||
      this.props.outcomeUnknown || this.props.temporaryUnavailable || !['ready', 'setup_required'].includes(this.props.recoveryStatus) || !this.props.saveTemporary) return;
    this.sending = true; this.setState({ pending: true });
    let result;
    try { result = await this.props.saveTemporary(); } catch (_) { result = { state: 'uncertain' }; }
    this.sending = false;
    if (this.mounted) this.setState({ pending: false, result: result || { state: 'invalid' } });
  };
  render() {
    const {
      t, profileBusy, recoveryStatus: status, hasPendingChanges, outcomeUnknown,
      temporaryUnavailable, setupEmailDestination, emailBlocked, children,
    } = this.props;
    const { method, pending, result, cooldown, blocked, slow } = this.state;
    const busy = pending || profileBusy;
    const disabled = busy || outcomeUnknown || temporaryUnavailable || !['ready', 'setup_required'].includes(status);
    const messages = {
      accepted: 'Email sent to the mail service. Ask the user to check their inbox and spam folder.',
      cooldown: 'Please wait one minute before sending again.',
      daily_limit: 'Email limit reached. Use a manual temporary password or try tomorrow.',
      unavailable: 'Email is unavailable. Check account status and try again.',
      not_eligible: 'Account status changed. Check account status before continuing.',
      uncertain: 'The result is unconfirmed. Contact support before trying again.',
      saved: 'Temporary password saved. Share it privately with the user.',
      invalid: 'Check the password and required profile fields, then try again.',
    };
    return (
      <section style={{ paddingBottom: 8 }} aria-label={t('Password recovery')} tabIndex={-1} ref={(ref) => { this.section = ref; }}>
        <Divider style={{ margin: '12px 0' }} />
        <div style={{ fontWeight: 700, marginBottom: 8 }}>{t('Password recovery')}</div>
        {status === 'setup_required' && <p role="status">{t('Password change required at sign-in')}</p>}
        {status === 'checking' && <p role="status">{t('Checking account status…')}</p>}
        {status === 'unavailable' && <p>{t('Cannot confirm account status. Try again.')}</p>}
        {(status === 'company_sign_in' || temporaryUnavailable) && <p>{t('For company-managed sign-in, ask the company IT team to reset the password.')}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          <Button type="button" style={{ margin: 0 }} basic disabled={disabled || hasPendingChanges || !setupEmailDestination || cooldown || blocked || emailBlocked} onClick={e => this.choose('email', e.currentTarget)}>
            {t('Email temporary password')} <Label size="tiny">{t('Recommended')}</Label>
          </Button>
          <Button type="button" style={{ margin: 0 }} basic disabled={disabled || hasPendingChanges} onClick={e => this.choose('temporary', e.currentTarget)}>{t('Set a temporary password')}</Button>
          {this.props.refreshStatus && (status !== 'ready' || !setupEmailDestination) && <Button type="button" basic style={{ margin: 0 }} disabled={busy} onClick={this.props.refreshStatus}>{t('Check account status')}</Button>}
        </div>
        {['ready', 'setup_required'].includes(status) && !setupEmailDestination && <p>{t('Email is unavailable. Use a manual temporary password.')}</p>}
        {hasPendingChanges && !method && <p>{t('Save or cancel profile changes first.')}</p>}
        <Modal
          size="tiny" open={!!method} role="dialog" {...{ 'aria-modal': true }} aria-busy={busy} aria-labelledby="password-recovery-review-title"
          onClose={this.close}
          onUnmount={() => {
            if (this.reviewTrigger && this.reviewTrigger.isConnected &&
              !this.reviewTrigger.disabled) {
              this.reviewTrigger.focus();
            } else if (this.section) this.section.focus();
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Tab') return;
            const items = Array.from(e.currentTarget.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]'));
            if (!items.length) { e.preventDefault(); return; }
            const first = items[0]; const last = items[items.length - 1];
            const fromFirst = document.activeElement === first ||
              document.activeElement === this.resultPanel;
            if (e.shiftKey && fromFirst) {
              e.preventDefault(); last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault(); first.focus();
            }
          }}
        >
          <Modal.Header id="password-recovery-review-title">{t(method === 'temporary' ? 'Set a temporary password' : 'Email temporary password')}</Modal.Header>
          <Modal.Content>
            {!result && method === 'email' && <div>
              <p>{t('Send to')}: <strong>{setupEmailDestination}</strong></p>
              <p>{t('This replaces the current password. The user signs in with the email and chooses a new password.')}</p>
            </div>}
            {method === 'temporary' && !temporaryUnavailable && ['ready', 'setup_required'].includes(status) && (!result || result.state === 'invalid') && <div>
              <p>{t('Enter a temporary password. The user must change it at sign-in.')}</p>
              <fieldset disabled={busy} style={{ border: 0, margin: 0, padding: 0 }}>
                {children}
              </fieldset>
            </div>}
            <div aria-live="polite" tabIndex={-1} ref={(ref) => { this.resultPanel = ref; }}>
              {busy && <p role="status">{t(method === 'email' ? 'Sending email…' : 'Saving password…')}</p>}
              {slow && <Message warning>{t('The request is still pending. Do not resend or switch methods until it finishes.')}</Message>}
              {result && <Message style={{ display: 'block' }} warning={!['accepted', 'saved'].includes(result.state)}>
                <p>{t(result.state === 'uncertain' && method === 'email' ? 'Email delivery is unconfirmed. Check the inbox before using a manual temporary password.' : messages[result.state] || messages.uncertain)}</p>
                {result.state === 'invalid' && result.errors && <ul>{result.errors.map((error, index) => <li key={index}>{error}</li>)}</ul>}
                {result.destination && <p>{result.destination}</p>}
                {result.state === 'accepted' && <p>{t('Use the Sign in to MHub button in the email. Choose a new password when asked.')}</p>}
                {cooldown && <p>{t('Please wait one minute before sending again.')}</p>}
              </Message>}
              {outcomeUnknown && !result && <p>{t(messages.uncertain)}</p>}
            </div>
          </Modal.Content>
          <Modal.Actions>
            <Button type="button" autoFocus disabled={busy} onClick={this.close}>{t(result ? 'Close' : 'Cancel')}</Button>
            {(!result || result.state === 'invalid') && <Button type="button" primary loading={busy} disabled={busy || outcomeUnknown || temporaryUnavailable || (method === 'email' && (hasPendingChanges || cooldown || blocked || emailBlocked || !setupEmailDestination))} onClick={method === 'email' ? this.send : this.saveManual}>{t(method === 'email' ? 'Send email' : 'Save temporary password')}</Button>}
          </Modal.Actions>
        </Modal>
        {this.props.passwordUpdateStatus && (
          <div style={{ marginTop: 12 }}>{this.props.passwordUpdateStatus}</div>
        )}
      </section>
    );
  }
}
export default PasswordRecoveryMethods;



// WEBPACK FOOTER //
// ./src/components/PasswordRecoveryMethods.jsx